import { FUNCTIONS_BASE_URL, IS_LOCAL_FUNCTIONS } from '../config/apiConfig';
import { SessionValidationResponse } from '../types';

const API_BASE = FUNCTIONS_BASE_URL;
const ENABLE_UPLOAD_ANALYSIS =
  process.env.REACT_APP_ENABLE_UPLOAD_ANALYSIS === 'true' ||
  (!IS_LOCAL_FUNCTIONS && process.env.REACT_APP_ENABLE_UPLOAD_ANALYSIS !== 'false');

export interface CreateWebSessionRequest {
  email: string;
  phoneNumber?: string;
  personId?: string;
  sessionTitle?: string;
  bookType?: string;
  source?: string;
}

export interface CreateWebSessionResponse {
  id: string;
  key: string;
}

export interface UpdateWebSessionRequest {
  sessionId: string;
  sessionTitle?: string;
  phoneNumber?: string;
  bookSize?: string;
  coverType?: string;
  specialInstructions?: string;
}

export class SessionAPI {
  static async createWebSession(
    request: CreateWebSessionRequest,
  ): Promise<CreateWebSessionResponse | null> {
    try {
      const response = await fetch(`${API_BASE}/createSession`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: request.email.trim(),
          sessionTitle: request.sessionTitle || 'Classic Studio Book',
          bookType: request.bookType || 'Classic',
          phoneNumber: request.phoneNumber,
          personId: request.personId,
          source: request.source || 'web',
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ createWebSession failed:', response.status, errorText);
        return null;
      }

      const data = await response.json() as CreateWebSessionResponse;
      if (!data.id || !data.key) {
        console.error('❌ createWebSession missing id/key:', data);
        return null;
      }

      return data;
    } catch (error) {
      console.error('❌ createWebSession error:', error);
      return null;
    }
  }

  static async updateWebSession(request: UpdateWebSessionRequest): Promise<boolean> {
    if (!request.sessionId) {
      return false;
    }

    try {
      const response = await fetch(`${API_BASE}/updateSessionMetadata`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ updateWebSession failed:', response.status, errorText);
        return false;
      }

      return true;
    } catch (error) {
      console.error('❌ updateWebSession error:', error);
      return false;
    }
  }

  static async validateSession(sessionId: string, key: string): Promise<SessionValidationResponse> {
    console.log('🔍 Validating session:', { sessionId, key: key ? 'PROVIDED' : 'EMPTY' });
    
    if (!key || key.trim() === '') {
      throw new Error('Session key is required');
    }

    try {
      const response = await fetch(`${API_BASE}/validateSession`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: sessionId,
          key: key,
        }),
      });

      console.log('📥 Session validation response:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Session validation failed (${response.status}): ${errorText}`);
      }

      const data: SessionValidationResponse = await response.json();
      console.log('✅ Session validation result:', { valid: data.valid, hasPrompt: !!data.prompt });
      
      return data;
      
    } catch (error) {
      console.error('❌ Session validation error:', error);
      throw error;
    }
  }

  static async updateSessionStatus(sessionId: string, key: string, status: string): Promise<void> {
    console.log('📝 Updating session status:', { sessionId, status });
    
    try {
      const response = await fetch(`${API_BASE}/updateSessionStatus`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: sessionId,
          key: key,
          status: status,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.ok) {
          console.log('✅ Session status updated successfully:', status);
        } else {
          console.warn('⚠️ Session status update failed:', data.message);
        }
      } else {
        const errorText = await response.text();
        console.error('❌ Session status update HTTP error:', response.status, errorText);
      }
    } catch (error) {
      console.error('❌ Session status update error:', error);
    }
  }

  static async markSessionComplete(sessionId: string, key: string, numPhotos: number): Promise<void> {
    console.log('🏁 Marking session complete:', { sessionId, numPhotos });
    
    try {
      const response = await fetch(`${API_BASE}/markSessionComplete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: sessionId,
          key: key,
          numPhotos: numPhotos,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.ok) {
          console.log('✅ Session marked complete successfully:', numPhotos, 'photos');
        } else {
          console.warn('⚠️ Failed to mark session complete:', data.message);
        }
      } else {
        const errorText = await response.text();
        console.error('❌ Mark session complete HTTP error:', response.status, errorText);
      }
    } catch (error) {
      console.error('❌ Mark session complete error:', error);
    }
  }

  static async getNextBatchNumber(sessionId: string, key: string): Promise<number> {
    console.log('🔢 Getting next batch number for session:', sessionId);
    
    try {
      const response = await fetch(`${API_BASE}/getNextBatchNumber`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: sessionId,
          key: key,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          console.log('✅ Next batch number retrieved:', data.nextBatchNumber);
          return data.nextBatchNumber || 1;
        } else {
          console.warn('⚠️ Failed to get batch number:', data.error);
          return 1;
        }
      } else {
        const errorText = await response.text();
        console.warn('⚠️ Batch number HTTP error:', response.status, errorText);
        return 1;
      }
    } catch (error) {
      console.warn('⚠️ Batch number request error:', error);
      return 1;
    }
  }

  static async saveUploadMetadata(
    uploadData: {
      uploadId: string;
      sessionId: string;
      key: string;
      filename: string;
      url: string;
      cloudinaryPublicId?: string;
      s3Key?: string;
      uploadSource?: string;
      fileSize: number;
      isHeicOriginal: boolean;
      hasMetadata: boolean;
      hasCreationDate: boolean;
      hasLocation: boolean;
      createdAt?: string;
      latitude?: number;
      longitude?: number;
      batchNumber: number;
      originalFilename: string;
    }
  ): Promise<string | null> {
    console.log('💾 Saving upload metadata:', uploadData.filename);
    
    try {
      const requestBody = {
        uploadId: uploadData.uploadId,
        sessionId: uploadData.sessionId,
        key: uploadData.key,
        filename: uploadData.filename,
        originalFilename: uploadData.originalFilename,
        url: uploadData.url,
        cloudinaryPublicId: uploadData.cloudinaryPublicId,
        s3Key: uploadData.s3Key,
        uploadSource: uploadData.uploadSource,
        fileSize: uploadData.fileSize,
        isHeicOriginal: uploadData.isHeicOriginal,
        hasMetadata: uploadData.hasMetadata,
        clientExtractMetadata: true,
        hasCreationDate: uploadData.hasCreationDate,
        hasLocation: uploadData.hasLocation,
        createdAt: uploadData.createdAt,
        latitude: uploadData.latitude,
        longitude: uploadData.longitude,
        batchNumber: uploadData.batchNumber,
      };
      
      console.log('💾 Upload metadata request:', requestBody);
      
      const response = await fetch(`${API_BASE}/saveUploadMetadata`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      console.log('💾 Upload metadata response:', response.status);

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          console.log('✅ Upload metadata saved successfully:', uploadData.filename);
          
          // Trigger analysis if we got an upload document ID
          const uploadDocId = data.uploadDocId;
          if (uploadDocId && ENABLE_UPLOAD_ANALYSIS) {
            console.log('🔍 Triggering analysis for upload:', uploadDocId);
            // Fire-and-forget analysis (don't block the upload flow)
            this.analyzeUpload(uploadDocId).catch(error => {
              console.warn('⚠️ Analysis failed for upload:', uploadDocId, error);
            });
          } else if (uploadDocId) {
            console.log('⏭️ Skipping upload analysis for local/emulator upload:', uploadDocId);
          }
          
          return uploadDocId;
        } else {
          console.error('❌ Failed to save upload metadata:', data.error);
          return null;
        }
      } else {
        const errorText = await response.text();
        console.error('❌ Upload metadata HTTP error:', response.status, errorText);
        return null;
      }
    } catch (error) {
      console.error('❌ Upload metadata save error:', error);
      return null;
    }
  }

  // Batched metadata saving - saves multiple uploads in a single API call
  static async saveUploadMetadataBatch(
    uploads: Array<{
      uploadId: string;
      sessionId: string;
      key: string;
      filename: string;
      url: string;
      cloudinaryPublicId?: string;
      s3Key?: string;
      uploadSource?: string;
      fileSize: number;
      isHeicOriginal: boolean;
      hasMetadata: boolean;
      hasCreationDate: boolean;
      hasLocation: boolean;
      createdAt?: string;
      latitude?: number;
      longitude?: number;
      batchNumber: number;
      originalFilename: string;
    }>
  ): Promise<{ success: boolean; uploadDocIds: string[] }> {
    console.log(`📦 Saving batch of ${uploads.length} upload metadata records`);
    
    try {
      // For now, call individual endpoints in parallel since we may not have a batch endpoint
      // This still reduces overhead by parallelizing the network calls
      const promises = uploads.map(uploadData => this.saveUploadMetadata(uploadData));
      const results = await Promise.all(promises);
      
      const uploadDocIds = results.filter((id): id is string => id !== null);
      const success = uploadDocIds.length === uploads.length;
      
      if (success) {
        console.log(`✅ Batch metadata saved successfully: ${uploadDocIds.length}/${uploads.length} uploads`);
      } else {
        console.warn(`⚠️ Batch metadata partially saved: ${uploadDocIds.length}/${uploads.length} uploads succeeded`);
      }
      
      return { success, uploadDocIds };
      
    } catch (error) {
      console.error('❌ Batch metadata save error:', error);
      return { success: false, uploadDocIds: [] };
    }
  }

  static async analyzeUpload(uploadDocId: string): Promise<boolean> {
    console.log('🔍 Starting upload analysis:', uploadDocId);
    
    try {
      const response = await fetch(`${API_BASE}/analyzeUpload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          uploadId: uploadDocId,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Upload analysis result:', data.success);
        return data.success;
      } else {
        const errorText = await response.text();
        console.warn('⚠️ Upload analysis HTTP error:', response.status, errorText);
        return false;
      }
    } catch (error) {
      console.warn('⚠️ Upload analysis error:', error);
      return false;
    }
  }

  // Test mode support
  static isTestMode(sessionId: string, key: string): boolean {
    return sessionId === 'test' && key === 'test';
  }

  static createTestSession(): SessionValidationResponse {
    console.log('🧪 Creating test session');
    return {
      valid: true,
      prompt: 'Test session for development and debugging',
      bookType: 'Classic',
      minPhotos: 20,
      maxPhotos: 200,
    };
  }

  // Enhanced error handling
  static handleApiError(error: any, operation: string): never {
    console.error(`❌ API Error during ${operation}:`, error);
    
    if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
      throw new Error(`Network error during ${operation}. Please check your internet connection.`);
    } else if (error.message.includes('timeout')) {
      throw new Error(`Request timeout during ${operation}. Please try again.`);
    } else if (error.message.includes('401') || error.message.includes('403')) {
      throw new Error(`Authentication failed during ${operation}. Please check your session link.`);
    } else if (error.message.includes('500')) {
      throw new Error(`Server error during ${operation}. Please try again later.`);
    } else {
      throw new Error(`${operation} failed: ${error.message}`);
    }
  }

  // Retry mechanism for critical operations
  static async withRetry<T>(
    operation: () => Promise<T>,
    maxRetries: number = 3,
    delayMs: number = 1000,
    operationName: string = 'API operation'
  ): Promise<T> {
    let lastError: any;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`🔄 ${operationName} attempt ${attempt}/${maxRetries}`);
        return await operation();
      } catch (error) {
        lastError = error;
        console.warn(`⚠️ ${operationName} attempt ${attempt} failed:`, error);
        
        if (attempt < maxRetries) {
          const delay = delayMs * Math.pow(2, attempt - 1); // Exponential backoff
          console.log(`⏳ Retrying ${operationName} in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
    
    this.handleApiError(lastError, operationName);
  }
}
